import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { FullUser, PublicUser } from "@/types";
import { showErrorToast } from "@/lib/error-handling";
import {
  cancelUserInvitation,
  CancelUserInvitationResponse,
  createUser,
  CreateUserResponse,
  deactivateUser,
  fetchFullUsers,
  fetchPublicUsers,
  fetchUserById,
  reactivateUser,
  sendUserInvitation,
  updateUser,
  updateUserProfile,
  UpdateUserRequest,
} from "@/services/frontendServices/usersServices";
import { fetchCurrentUser } from "@/services/frontendServices/authService";
import { UserFormData } from "@/components/forms/create-user-form";
import { OrganizationUserStatus } from "@/enums";
import { actionItemKeys } from "./useActionItems";
import { describeAddUserOutcome } from "./addUserOutcomeToast";
import { describeCancelInvitationResult } from "./cancelInvitationToast";

export type UserStatusFilter =
  | OrganizationUserStatus
  | OrganizationUserStatus[];

export const usersKeys = {
  all: ["users"] as const,
  lists: () => [...usersKeys.all, "list"] as const,
  list: (organizationId: string) =>
    [...usersKeys.lists(), organizationId] as const,
  public: (status?: UserStatusFilter) =>
    [...usersKeys.lists(), "public", status] as const,
  details: () => [...usersKeys.all, "detail"] as const,
};

export const useUsers = (status?: UserStatusFilter) => {
  return useQuery({
    queryKey: usersKeys.public(status),
    queryFn: () => fetchPublicUsers(status),
    enabled: true,
  });
};

export const useOrganizationUsers = () => {
  return useQuery({
    queryKey: usersKeys.lists(),
    queryFn: () => fetchFullUsers(),
    enabled: true,
  });
};

export const useCurrentUser = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["currentUser"],
    queryFn: fetchCurrentUser,
    retry: false,
    enabled: options?.enabled ?? true,
  });
};

export const useUser = (userId: string) => {
  return useQuery({
    queryKey: ["user", userId],
    queryFn: () => fetchUserById(userId),
    enabled: !!userId,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateUserResponse, Error, UserFormData>({
    mutationFn: ({ ...formData }) => createUser(formData),
    onSuccess: (newUser) => {
      // Update the individual user detail
      queryClient.setQueryData(["user", newUser.id], newUser);

      // Invalidate and refetch organization data to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["user", newUser.id] });
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      queryClient.invalidateQueries({ queryKey: ["departments"] });

      const { level, title, description } = describeAddUserOutcome(
        newUser.outcome
      );
      if (level === "warning") {
        toast.warning(title, { description, duration: 6000 });
      } else {
        toast.success(title, { description, duration: 3000 });
      }
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error inviting user",
        defaultMessage: "Failed to invite user",
        duration: 5000,
      });
    },
  });
};

const useUserMutation = (
  mutationFn: (args: {
    userId: string;
    formData: UpdateUserRequest;
  }) => Promise<PublicUser>,
  successMessage: string,
  errorMessage: string
) => {
  const queryClient = useQueryClient();

  return useMutation<
    PublicUser,
    Error,
    { userId: string; formData: UpdateUserRequest }
  >({
    mutationFn,
    onSuccess: (updatedUser, { userId }) => {
      queryClient.setQueryData(["user", userId], updatedUser);
      queryClient.setQueryData(["currentUser"], updatedUser);

      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });

      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });

      // Invalidate departments since user's department membership changed
      queryClient.invalidateQueries({ queryKey: ["departments"] });

      toast.success(successMessage, {
        description: `User has been updated.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error updating user",
        defaultMessage: errorMessage || "Failed to update user",
        duration: 5000,
      });
    },
  });
};

export const useUpdateUser = () => {
  return useUserMutation(
    ({ userId, formData }) => updateUser(userId, formData),
    "User updated successfully!",
    "Failed to update user"
  );
};

export const useUpdateUserProfile = () => {
  return useUserMutation(
    ({ userId, formData }) => updateUserProfile(userId, formData),
    "User Profile updated successfully!",
    "Failed to update user profile"
  );
};

const useUserActionMutation = (
  mutationFn: (
    userId: string,
    reassignedToUserId?: string
  ) => Promise<{ id: string; status: OrganizationUserStatus }>,
  successMessage: string,
  errorMessage: string
) => {
  const queryClient = useQueryClient();

  return useMutation<
    { id: string; status: OrganizationUserStatus },
    Error,
    { userId: string; reassignedToUserId?: string }
  >({
    mutationFn: ({ userId, reassignedToUserId }) =>
      mutationFn(userId, reassignedToUserId),
    onSuccess: (result, { userId, reassignedToUserId }) => {
      // Update user detail cache with new status
      queryClient.setQueryData(["user", userId], (old: FullUser) => ({
        ...old,
        status: result.status,
      }));

      // Invalidate lists and detail queries
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });

      // Invalidate resources that have users as owners
      queryClient.invalidateQueries({ queryKey: ["actionItems"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      queryClient.invalidateQueries({
        queryKey: actionItemKeys.openByUser(userId),
      });

      // Invalidate reassigned user data if applicable
      if (reassignedToUserId) {
        queryClient.invalidateQueries({
          queryKey: actionItemKeys.openByUser(reassignedToUserId),
        });
        queryClient.invalidateQueries({ queryKey: actionItemKeys.all });
      }

      toast.success(successMessage, {
        description: `User status updated.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error updating user status",
        defaultMessage: errorMessage || "Failed to update user status",
        duration: 5000,
      });
    },
  });
};

export const useDeactivateUser = () => {
  return useUserActionMutation(
    deactivateUser,
    "User deactivated successfully!",
    "Failed to deactivate user"
  );
};

export const useReactivateUser = () => {
  return useUserActionMutation(
    reactivateUser,
    "User reactivated successfully!",
    "Failed to reactivate user"
  );
};

export const useSendInvitation = () => {
  return useMutation<{ success: boolean; message: string }, Error, string>({
    mutationFn: (userId: string) => sendUserInvitation(userId),
    onSuccess: () => {
      toast.success("Invitation sent successfully!", {
        description: "A new invitation email has been sent to the user.",
        duration: 3000,
      });
    },
    onError: (error) => {
      toast.error("Error sending invitation", {
        description: error.message,
        duration: 5000,
      });
    },
  });
};

export const useCancelInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation<CancelUserInvitationResponse, Error, string>({
    mutationFn: (userId: string) => cancelUserInvitation(userId),
    onSuccess: (result, userId) => {
      // The membership is soft-deleted, so the row disappears from the list and
      // the user detail query no longer resolves — invalidate both, plus
      // departments, whose membership links were soft-deleted too.
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      queryClient.invalidateQueries({ queryKey: ["departments"] });

      toast.success("Invitation cancelled", {
        description: describeCancelInvitationResult(result),
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error cancelling invitation",
        defaultMessage: "Failed to cancel invitation",
        duration: 5000,
      });
    },
  });
};
